import { Link } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import { CalendarDays, Clock, Image as ImageIcon, Sparkles, Trophy } from "lucide-react";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { useTournaments } from "@/hooks/useTournaments";
import { api, type SocialCalendarDay } from "@/lib/api";
import { formatItalianDate, daysBetween } from "@/lib/format";
import { cn } from "@/lib/utils";
import dayjs from "dayjs";

export default function GlobalCalendar() {
  const tournaments = useTournaments({ page_size: 200 });

  // Tornei attivi: non completed/failed, data futura
  const activeTournaments = (tournaments.data?.items ?? []).filter((t) => {
    const status = (t.status ?? "").toUpperCase();
    return (
      status !== "COMPLETED" &&
      status !== "FAILED" &&
      new Date(t.date) >= new Date(new Date().setHours(0, 0, 0, 0))
    );
  });

  // Fetch calendar per ogni torneo attivo
  const calendars = useQueries({
    queries: activeTournaments.map((t) => ({
      queryKey: ["social-calendar", t.slug],
      queryFn: () => api.socialCalendar(t.slug),
      enabled: true,
    })),
  });

  const today = dayjs().format("YYYY-MM-DD");
  const tomorrow = dayjs().add(1, "day").format("YYYY-MM-DD");

  type Uscita = {
    tournament: (typeof activeTournaments)[number];
    day: SocialCalendarDay;
    when: "oggi" | "domani";
  };

  const uscite: Uscita[] = [];
  activeTournaments.forEach((t, idx) => {
    const days = calendars[idx]?.data ?? [];
    const todayDay = days.find((d) => d.date === today);
    const tomorrowDay = days.find((d) => d.date === tomorrow);
    if (todayDay && todayDay.actions.length > 0) {
      uscite.push({ tournament: t, day: todayDay, when: "oggi" });
    }
    if (tomorrowDay && tomorrowDay.actions.length > 0) {
      uscite.push({ tournament: t, day: tomorrowDay, when: "domani" });
    }
  });

  const usciteOggi = uscite.filter((u) => u.when === "oggi");
  const usciteDomani = uscite.filter((u) => u.when === "domani");

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
          <CalendarDays className="h-6 w-6 text-moving-500" />
          Calendario social
        </h1>
        <p className="text-sm text-slate-500">
          Cosa esce sui canali per i tornei attivi
        </p>
      </div>

      {/* Oggi */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-moving-500" />
              Oggi · {formatItalianDate(new Date(), true)}
            </CardTitle>
            <span className="text-xs text-slate-500">
              cron: 11:00 &amp; 18:00
            </span>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {tournaments.isLoading ? (
            <div className="p-5 text-sm text-slate-500">Caricamento…</div>
          ) : usciteOggi.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-slate-500">
              Nessuna uscita schedulata per oggi.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {usciteOggi.map((u, i) => (
                <UscitaRow key={i} tournament={u.tournament} day={u.day} />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {/* Domani */}
      <Card>
        <CardHeader>
          <CardTitle>Domani · {formatItalianDate(dayjs().add(1, "day").toDate(), true)}</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {usciteDomani.length === 0 ? (
            <div className="px-5 py-6 text-center text-sm text-slate-500">
              Niente schedulato per domani.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {usciteDomani.map((u, i) => (
                <UscitaRow key={i} tournament={u.tournament} day={u.day} />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {/* Lista tornei attivi */}
      <Card>
        <CardHeader>
          <CardTitle>Tornei nel calendario</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {activeTournaments.length === 0 ? (
            <div className="px-5 py-6 text-center text-sm text-slate-500">
              Nessun torneo attivo.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {activeTournaments.map((t) => {
                const days = daysBetween(new Date(), t.date);
                return (
                  <li key={t.slug}>
                    <Link
                      to={`/tournaments/${t.slug}/calendar`}
                      className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50"
                    >
                      <Trophy className="h-4 w-4 text-slate-400" />
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-sm font-medium text-slate-900">
                          {t.name}
                        </div>
                        <div className="text-xs text-slate-500">
                          {formatItalianDate(t.date)} · fra {days}g
                        </div>
                      </div>
                      <span className="text-xs text-court-600">
                        Vedi calendario →
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function UscitaRow({
  tournament,
  day,
}: {
  tournament: { slug: string; name: string };
  day: SocialCalendarDay;
}) {
  return (
    <li className="flex items-start gap-3 px-4 py-3 md:px-5 md:py-4">
      <div className="flex-1 min-w-0">
        <Link
          to={`/tournaments/${tournament.slug}`}
          className="truncate text-sm font-medium text-slate-900 hover:text-court-600"
        >
          {tournament.name}
        </Link>
        <div className="mt-0.5 text-xs text-slate-500">
          D-{day.days_to_tournament} al torneo
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {day.actions.map((a) => {
            const done = day.published.some((p) =>
              p.toLowerCase().includes(a.toLowerCase()),
            );
            return (
              <span
                key={a}
                className={cn(
                  "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium",
                  done
                    ? "bg-moving-100 text-moving-700"
                    : "bg-slate-100 text-slate-700",
                )}
              >
                {a === "post" ? (
                  <ImageIcon className="h-3 w-3" />
                ) : (
                  <Sparkles className="h-3 w-3" />
                )}
                {a}
                {done ? " ✓" : ""}
              </span>
            );
          })}
        </div>
      </div>
    </li>
  );
}
